import React, { useEffect } from "react";

import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import makeStyles from "@mui/styles/makeStyles";

import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";

import { WorkflowV1 } from "../../shared/interfaces/WorkflowV1";

import { Col, Row } from "react-bootstrap";
import { Card } from "../../shared/Card";
import { FormRow } from "../../shared/FormLayout/FormRow";
import { ValidationMessage } from "../../shared/ValidationMessage";
import { WorkflowV2 } from "../../shared/interfaces/WorkflowV2";

interface Props {
  workflow: WorkflowV1 | WorkflowV2;
  editMode?: boolean;
  isNewWorkflow?: boolean;
  onWorkflowFieldChange?: (field: string, value: string) => void;
}

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
}));

export function WorkflowTab(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    register,
    formState: { errors },
    clearErrors,
    setValue,
  } = useFormContext();

  const handleDescriptionInputChange = (event: any) => {
    clearErrors("workflowTab.description");

    const value = event.target.value;
    props.onWorkflowFieldChange?.("description", value);
  };

  useEffect(() => {
    setValue("workflowTab.description", props.workflow.description ?? "");
  }, [props.workflow.description, setValue]);

  const handleInputChange = (event: any) => {
    const name = event.target.name;
    const value = event.target.value;

    props.onWorkflowFieldChange?.(name, value);
  };

  return (
    <React.Fragment>
      <Row className="d-flex justify-content-center align-items-center">
        <Col lg={6}>
          <Card title={t("workflows.workflowDetails")} className="mb-3">
            <FormRow
              label={t("common.summary")}
              required={true}
              value={
                props.editMode ? (
                  <div className={classes.root}>
                    <InputText
                      className="w-100"
                      maxLength={200}
                      {...register("workflowTab.description", {
                        required: true,
                        value: props.workflow.description,
                        onChange: handleDescriptionInputChange,
                      })}
                    />
                    {errors.workflowTab?.description && (
                      <ValidationMessage
                        invalidMessage={t("workflows.workflowSummaryRequired")}
                        validationType="error"
                        showIcon
                      />
                    )}
                  </div>
                ) : (
                  props.workflow.description
                )
              }
            />
            <FormRow
              label={t("common.detailedDescription")}
              value={
                props.editMode ? (
                  <div className={classes.root}>
                    <InputTextarea
                      className="w-100"
                      name="longDescription"
                      onChange={handleInputChange}
                      value={props.workflow.longDescription}
                    />
                  </div>
                ) : (
                  props.workflow.longDescription
                )
              }
            />
            {!props.isNewWorkflow && (
              <>
                <FormRow
                  label={t("common.createdDate")}
                  value={t("common.formattedDate", {
                    dateString: props.workflow.created,
                  })}
                />
                <FormRow label={t("common.createdBy")} value={props.workflow.createdByUserName} />
                <FormRow label={t("workflows.revision")} value={props.workflow.revisionNo} />
                <FormRow
                  label={t("common.updatedDate")}
                  value={t("common.formattedDate", {
                    dateString: props.workflow.updated,
                  })}
                />
              </>
            )}
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
}
